import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Edit, Search, Filter, Download } from 'lucide-react';
import { medicineService } from '../api/medicines';
import { medicineSchema, type MedicineFormData } from '../schemas/medicineSchema';
import type { Medicine } from '../types';
import { exportToCSV } from '../../../lib/csvExport';

export default function Medicines() {
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [filteredMedicines, setFilteredMedicines] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState<Medicine | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<MedicineFormData>({
    resolver: zodResolver(medicineSchema),
    defaultValues: {
      status: 'active',
      reorderLevel: 0
    }
  });

  useEffect(() => {
    loadMedicines();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      filterMedicines();
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(timeoutId);
  }, [medicines, searchQuery, statusFilter]);

  const loadMedicines = async () => {
    try {
      const data = await medicineService.getAll();
      setMedicines(data);
    } catch (error) {
      console.error('Failed to load medicines:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMedicines = async () => {
    try {
      if (searchQuery.trim()) {
        setLoading(true);
        const results = await medicineService.search(searchQuery.trim(), statusFilter);
        setFilteredMedicines(results);
        setLoading(false);
      } else {
        let filtered = medicines;

        if (statusFilter !== 'all') {
          filtered = filtered.filter(medicine => medicine.status === statusFilter);
        }

        setFilteredMedicines(filtered);
      }
    } catch (error) {
      console.error('Failed to filter medicines:', error);
      setLoading(false);
    }
  };

  const onSubmit = async (data: MedicineFormData) => {
    try {
      if (!editingMedicine && 'checkDuplicate' in medicineService) {
        const isDuplicate = await (medicineService as any).checkDuplicate(data.name);
        if (isDuplicate) {
          alert(`A medicine with the name "${data.name}" already exists. Please use a different name.`);
          return;
        }
      }
      
      if (editingMedicine) {
        await medicineService.update(editingMedicine.id, data);
      } else {
        await medicineService.create(data);
      }
      
      await loadMedicines();
      setShowForm(false);
      setEditingMedicine(null);
      reset();
    } catch (error) {
      console.error('Failed to save medicine:', error);
    }
  };

  const handleExportCSV = () => {
    exportToCSV(
      filteredMedicines,
      'medicines',
      [
        { key: 'name', header: 'Medicine Name' },
        { key: 'genericName', header: 'Generic Name' },
        { key: 'brandName', header: 'Brand Name' },
        { key: 'strength', header: 'Strength' },
        { key: 'form', header: 'Form' },
        { key: 'route', header: 'Route' },
        { key: 'manufacturer', header: 'Manufacturer' },
        { key: 'reorderLevel', header: 'Reorder Level' },
        { key: 'status', header: 'Status' }
      ]
    );
  };

  const handleEdit = (medicine: Medicine) => {
    setEditingMedicine(medicine);
    reset({
      name: medicine.name,
      genericName: medicine.genericName || '',
      brandName: medicine.brandName || '',
      strength: medicine.strength,
      form: medicine.form,
      route: medicine.route,
      manufacturer: medicine.manufacturer || '',
      reorderLevel: medicine.reorderLevel,
      status: medicine.status
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMedicine(null);
    reset();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Medicines</h1>
          <p className="text-sm text-neutral-600">Manage medicine master data</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Medicine
          </button>
        </div>
      </header>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search medicines..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-neutral-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Medicine Form */}
      {showForm && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingMedicine ? 'Edit Medicine' : 'Add New Medicine'}
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Medicine Name *
                </label>
                <input
                  {...register('name')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Generic Name
                </label>
                <input
                  {...register('genericName')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Brand Name
                </label>
                <input
                  {...register('brandName')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Strength *
                </label>
                <input
                  {...register('strength')}
                  placeholder="e.g., 500mg"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.strength && (
                  <p className="text-sm text-red-600 mt-1">{errors.strength.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Form *
                </label>
                <select
                  {...register('form')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="tablet">Tablet</option>
                  <option value="capsule">Capsule</option>
                  <option value="syrup">Syrup</option>
                  <option value="injection">Injection</option>
                  <option value="ointment">Ointment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Route
                </label>
                <select
                  {...register('route')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select route</option>
                  <option value="oral">Oral</option>
                  <option value="iv">IV</option>
                  <option value="im">IM</option>
                  <option value="topical">Topical</option>
                  <option value="inhalation">Inhalation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Manufacturer
                </label>
                <input
                  {...register('manufacturer')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Reorder Level *
                </label>
                <input
                  type="number"
                  {...register('reorderLevel', { valueAsNumber: true })}
                  min="0"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.reorderLevel && (
                  <p className="text-sm text-red-600 mt-1">{errors.reorderLevel.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Status
                </label>
                <select
                  {...register('status')}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-neutral-700 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Saving...' : editingMedicine ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Medicines Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Medicine
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Strength
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Form
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Manufacturer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Reorder Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredMedicines.map((medicine) => (
                <tr key={medicine.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-neutral-900">{medicine.name}</div>
                      {medicine.genericName && (
                        <div className="text-sm text-neutral-500">Generic: {medicine.genericName}</div>
                      )}
                      {medicine.brandName && (
                        <div className="text-sm text-neutral-500">Brand: {medicine.brandName}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-900">{medicine.strength}</td>
                  <td className="px-6 py-4 text-sm text-neutral-900 capitalize">{medicine.form}</td>
                  <td className="px-6 py-4 text-sm text-neutral-900">{medicine.manufacturer || '-'}</td>
                  <td className="px-6 py-4 text-sm text-neutral-900">{medicine.reorderLevel}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      medicine.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {medicine.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleEdit(medicine)}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredMedicines.length === 0 && (
            <div className="text-center py-8">
              <p className="text-neutral-500">No medicines found</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
